import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
} from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import {
  SortableTable,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { peopleCoreAPI, financialSnapshotAPI, api } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Loader2, DollarSign, TrendingUp, TrendingDown, Edit } from 'lucide-react';

interface Partner {
  id: string;
  name: string;
  email: string;
  isPartner: boolean;
  partnershipPercentage: number | null;
  monthlyProfitAdvance: number | null;
}

interface ProfitAdvance {
  id: string;
  employeeId: string;
  month: number;
  year: number;
  amount: number;
  description: string | null;
  isAutomatic: boolean;
  employee: {
    user: {
      name: string;
      email: string;
    } | null;
  };
}

export default function ProfitAdvancesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Estado para mês/ano selecionado (padrão: mês atual)
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Estado para o modal de registro de antecipações
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [advanceValues, setAdvanceValues] = useState<Record<string, string>>({});

  // Query para buscar sócios
  const { data: partnersResponse, isLoading: isLoadingPartners } = useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      const response = await peopleCoreAPI.listPartners();
      return response.data;
    },
  });

  // Query para dados financeiros completos (usa a mesma API do dashboard)
  const { data: financialDataResponse, isLoading: isLoadingFinancialData } = useQuery({
    queryKey: ['financial-data', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await financialSnapshotAPI.getFinancialData(selectedMonth, selectedYear);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Query para buscar antecipações REAIS do mês
  const { data: advancesResponse, isLoading: isLoadingAdvances } = useQuery({
    queryKey: ['profit-advances', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await api.get(`/billing/profit-advances/month/${selectedMonth}/${selectedYear}`);
      return response.data;
    },
  });

  const partners = (partnersResponse?.data || []) as Partner[];
  const advances = (advancesResponse?.data || []) as ProfitAdvance[];

  // Dados financeiros completos do mês (mesma fonte do dashboard)
  const financialData = financialDataResponse?.data || {
    totalNetRevenue: 0,
    agencyProfit: 0,
    totalCommissions: 0,
    totalEmployeeCost: 0,
    totalProfitAdvances: 0,
    totalOperationalCosts: 0,
    taxAmount: 0,
    totalCost: 0,
    netProfit: 0,
    isSnapshot: false,
    closedAt: null,
  };

  // Mutation para salvar antecipações
  const saveAdvancesMutation = useMutation({
    mutationFn: async (values: Record<string, number>) => {
      // Deletar antecipações existentes do mês primeiro
      await api.delete(`/billing/profit-advances/month/${selectedMonth}/${selectedYear}`);

      // Criar novas antecipações
      const promises = Object.entries(values).map(([employeeId, amount]) => {
        if (amount > 0) {
          return api.post('/billing/profit-advances', {
            employeeId,
            month: selectedMonth,
            year: selectedYear,
            amount,
            description: 'Antecipação mensal',
            isAutomatic: false,
          });
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profit-advances', selectedMonth, selectedYear] });
      queryClient.invalidateQueries({ queryKey: ['financial-data', selectedMonth, selectedYear] });
      setIsModalOpen(false);
      setAdvanceValues({});
    },
  });

  // Calcular lucro ANTES das antecipações (mas depois de TODOS os outros custos)
  const totalRevenue = financialData.totalNetRevenue + financialData.agencyProfit;
  const totalCostsWithoutAdvances =
    financialData.totalCommissions +
    financialData.totalEmployeeCost +
    financialData.totalOperationalCosts +
    financialData.taxAmount;
  const profitBeforeAdvances = totalRevenue - totalCostsWithoutAdvances;

  // Agrupar antecipações por sócio
  const advancesByPartner = advances.reduce((acc, advance) => {
    if (!acc[advance.employeeId]) {
      acc[advance.employeeId] = 0;
    }
    acc[advance.employeeId] += advance.amount;
    return acc;
  }, {} as Record<string, number>);

  // Calcular total REAL de antecipações do mês
  const totalRealAdvances = advances.reduce((sum, advance) => sum + advance.amount, 0);

  // Preparar dados da tabela com cálculos usando antecipações REAIS
  const partnersData = partners.map((partner) => {
    const percentage = partner.partnershipPercentage || 0;
    const realAdvance = advancesByPartner[partner.id] || 0; // Antecipação REAL do banco
    const proportionalProfit = (profitBeforeAdvances * percentage) / 100;
    const balance = proportionalProfit - realAdvance;

    return {
      ...partner,
      proportionalProfit,
      realAdvance, // Mudamos de 'advance' para 'realAdvance'
      balance,
    };
  });

  // Lucro após antecipações REAIS
  const profitAfterAdvances = profitBeforeAdvances - totalRealAdvances;

  // Verificar se é admin e se o mês não está fechado
  const isAdmin = user?.role === 'ADMIN';
  const isClosed = financialData.isSnapshot && financialData.closedAt;
  const canEditAdvances = isAdmin && !isClosed;

  // Função para abrir o modal
  const handleOpenModal = () => {
    // Preencher valores existentes
    const initialValues: Record<string, string> = {};
    partnersData.forEach((partner) => {
      const existingAdvance = advancesByPartner[partner.id] || 0;
      initialValues[partner.id] = existingAdvance > 0 ? existingAdvance.toString() : '';
    });
    setAdvanceValues(initialValues);
    setIsModalOpen(true);
  };

  // Função para salvar antecipações
  const handleSaveAdvances = () => {
    const values: Record<string, number> = {};
    Object.entries(advanceValues).forEach(([employeeId, value]) => {
      const numValue = parseFloat(value || '0');
      if (!isNaN(numValue)) {
        values[employeeId] = numValue;
      }
    });
    saveAdvancesMutation.mutate(values);
  };

  // Função para atualizar valor de um sócio
  const handleAdvanceValueChange = (partnerId: string, value: string) => {
    setAdvanceValues((prev) => ({
      ...prev,
      [partnerId]: value,
    }));
  };

  if (isLoadingPartners || isLoadingFinancialData || isLoadingAdvances) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Antecipações de Lucro</h1>
          <p className="text-muted-foreground">
            Detalhamento de antecipações dos sócios
          </p>
        </div>

        <MonthYearSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          disableFutureMonths={true}
        />
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Lucro Antes Antecipações (100% dos custos já removidos) */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Lucro Sem Antecipações
                </p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                  {formatCurrency(profitBeforeAdvances)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Retira todos os Custos (exceto antecipações)
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Total de Antecipações Pagas */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Antecipações Pagas
                </p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                  {formatCurrency(totalRealAdvances)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Total pago no mês ({advances.length} antecipação{advances.length !== 1 ? 'ões' : ''})
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Lucro Final (após antecipações) */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Caixa
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {formatCurrency(profitAfterAdvances)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Caixa após antecipações pagas
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <TrendingDown className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Sócios */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Detalhamento por Sócio</CardTitle>
            {canEditAdvances && (
              <Button onClick={handleOpenModal} size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Registrar Antecipações
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {partnersData.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum sócio cadastrado
            </p>
          ) : (
            <SortableTable
              data={partnersData}
              defaultSortKey="partnershipPercentage"
              defaultSortDirection="desc"
            >
              {(sortedPartners) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead sortable sortKey="name">Sócio</TableHead>
                      <TableHead sortable sortKey="partnershipPercentage" align="center">% Participação</TableHead>
                      <TableHead sortable sortKey="proportionalProfit" align="right">Lucro Proporcional</TableHead>
                      <TableHead sortable sortKey="realAdvance" align="right">Valor Antecipado</TableHead>
                      <TableHead sortable sortKey="balance" align="right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPartners.map((partner) => {
                      const isPositiveBalance = partner.balance >= 0;

                      return (
                        <TableRow key={partner.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {partner.name}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {partner.email}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                                {partner.partnershipPercentage}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-right font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(partner.proportionalProfit)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-right font-semibold text-purple-600 dark:text-purple-400">
                              {formatCurrency(partner.realAdvance)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className={`text-right font-bold ${isPositiveBalance ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {formatCurrency(partner.balance)}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </SortableTable>
          )}
        </CardContent>
      </Card>

      {/* Modal de Registro de Antecipações */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Registrar Antecipações - {monthNames[selectedMonth - 1]}/{selectedYear}
            </DialogTitle>
            <DialogDescription>
              Informe o valor antecipado para cada sócio no mês.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 sm:p-6 space-y-4">
            {partnersData.map((partner) => (
              <div key={partner.id} className="space-y-2">
                <label htmlFor={`advance-${partner.id}`} className="text-sm font-medium text-card-foreground">
                  {partner.name}
                  <span className="text-xs text-muted-foreground ml-2">
                    (Lucro proporcional: {formatCurrency(partner.proportionalProfit)})
                  </span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    R$
                  </span>
                  <Input
                    id={`advance-${partner.id}`}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={advanceValues[partner.id] || ''}
                    onChange={(e) => handleAdvanceValueChange(partner.id, e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              disabled={saveAdvancesMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveAdvances}
              disabled={saveAdvancesMutation.isPending}
            >
              {saveAdvancesMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Antecipações'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
